/**
 * La franja que recuerda que estás dentro del negocio de un cliente.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  ESTO NO ES DECORACIÓN: ES LA MITAD DE LA SEGURIDAD DE ENTRAR
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Lo peligroso de poder entrar al negocio de un cliente no es entrar. Es
 * **olvidarse de que entraste**: dejas la pestaña abierta, vuelves media hora
 * después, y cambias un precio o cobras una venta creyendo que estás en tu
 * propio negocio. El cliente descubre una venta que él no hizo y no hay forma
 * de explicarle eso.
 *
 * Por eso la franja:
 *
 *   · **No se puede cerrar.** Un aviso con una X es un aviso que se cierra a
 *     los diez segundos y ya no protege de nada.
 *   · **Se queda pegado arriba al desplazar.** La primera versión iba en el
 *     flujo normal y se perdía de vista en cuanto bajabas — justo en el momento
 *     de riesgo, que es estar metido en una pantalla haciendo algo. Un aviso
 *     del que puedes alejarte scrolleando protege igual de poco que uno con
 *     una X.
 *   · **Dice el nombre del negocio.** «Estás en modo soporte» no sirve: el dato
 *     que evita el error es EN CUÁL estás.
 *   · **Lleva la salida al lado.** Si volver al panel costara buscar, la gente
 *     se quedaría dentro «un ratito más», que es justo el rato del accidente.
 *
 * El color es el de peligro y no el de la marca, a propósito. Tiene que
 * romper con el resto de la pantalla: si combinara bonito, en dos días
 * formaría parte del paisaje y dejaría de verse.
 */

import { useEffect, useRef } from "react";
import { LogOut, ShieldAlert } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/stores/auth";

export function AvisoDeSoporte() {
  const soporte = useAuth((s) => s.soporte);
  const tenant = useAuth((s) => s.tenant);
  const navegar = useNavigate();
  const franja = useRef<HTMLDivElement>(null);

  /*
    ⚠️ LA ALTURA SE MIDE, NO SE ADIVINA.

    La cabecera del móvil también es `sticky top-0`, así que tiene que
    colocarse justo debajo de esta franja. La primera versión le puso un
    `top-10` fijo dando por hecho que el aviso ocupa una línea — y en un
    teléfono ocupa dos, porque el nombre del negocio no cabe. La cabecera se
    metía debajo y quedaba medio tapada.

    Publicando la altura real, funciona con cualquier nombre y en cualquier
    ancho. Se vuelve a medir al cambiar de negocio y al girar el teléfono.
  */
  useEffect(() => {
    const raiz = document.documentElement;
    if (!soporte || !franja.current) {
      raiz.style.removeProperty("--alto-aviso");
      return;
    }

    const medir = () => {
      const alto = franja.current?.offsetHeight ?? 0;
      raiz.style.setProperty("--alto-aviso", `${alto}px`);
    };
    medir();

    const observador = new ResizeObserver(medir);
    observador.observe(franja.current);
    return () => {
      observador.disconnect();
      raiz.style.removeProperty("--alto-aviso");
    };
  }, [soporte, tenant?.name]);

  if (!soporte) return null;

  return (
    <div
      // `role="alert"` y no un `<div>` a secas: quien use lector de pantalla
      // tiene que enterarse igual de que no está en su negocio.
      ref={franja}
      role="alert"
      className="sticky top-0 z-40 flex flex-wrap items-center gap-x-3 gap-y-1.5 bg-danger px-4 py-2 text-white"
    >
      <ShieldAlert size={16} className="shrink-0" />

      {/*
        ⚠️ AQUÍ HABÍA UNA FRASE MÁS, Y SE QUITÓ PORQUE PASÓ A SER MENTIRA.

        Decía «lo que hagas aquí queda a nombre de Softfi en su registro», y era
        cierto cuando se escribió. Después se decidió que el soporte firme como
        el DUEÑO —ver `TenantContext.user_id`— y la frase se quedó afirmando lo
        contrario de lo que pasa.

        Un aviso que dice algo falso es peor que no tenerlo: enseña a no creerse
        el resto. Lo único que este cartel tiene que decir es en qué negocio
        estás, que es el dato que evita el error.
      */}
      <p className="min-w-0 flex-1 text-sm leading-snug">
        <span className="font-semibold">Estás dentro de un negocio ajeno:</span>{" "}
        <span className="font-semibold">{tenant?.name ?? "sin nombre"}</span>
      </p>

      <button
        type="button"
        onClick={() => navegar("/admin/negocios")}
        // Transparente con borde, no relleno: la franja ya grita bastante, y un
        // botón sólido encima competía con ella. El borde basta para que se lea
        // como algo que se toca.
        className="pulsable flex shrink-0 items-center gap-1.5 rounded-[var(--radius-control)] border border-white/45 px-2.5 py-1 text-xs font-medium transition-colors hover:border-white/70 hover:bg-white/10"
      >
        <LogOut size={13} />
        Volver al panel
      </button>
    </div>
  );
}
